"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useSettings } from "@/modules/shared/hooks/useSettings";
import { siteConfig } from "@/config/site.config";

export const Header: React.FC = () => {
  const { user, loginWithGoogle } = useAuth();
  const { preferences, setTheme, setFont, setLayout, setBionic } = useSettings();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCustomizerOpen, setIsCustomizerOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const customizerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        customizerRef.current &&
        !customizerRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsCustomizerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinks = siteConfig.menu;

  const handleAuthClick = (e: React.MouseEvent) => {
    if (!user) {
      e.preventDefault();
      setIsAuthModalOpen(true);
    }
  };

  const getFirstName = (fullName: string | null | undefined) => {
    if (!fullName) return "Mi Perfil";
    return fullName.split(" ")[0];
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 px-6 md:px-12 py-4 md:py-6 flex items-center justify-between transition-all duration-500 ${
          isScrolled
            ? "bg-bg-main/95 backdrop-blur-md border-b border-gold/20 shadow-xl shadow-black/30"
            : "bg-gradient-to-b from-bg-main/80 to-transparent"
        }`}
      >
        <Link href="/" className="flex items-center gap-3 group hover:opacity-90 transition-all duration-300">
          <Image
            src="/IMG/logo/logo-100.webp"
            alt="Escudo Copleston"
            width={52}
            height={52}
            className="object-contain filter drop-shadow-[0_0_12px_rgba(197,160,89,0.25)]"
          />
          <span className="font-serif text-lg md:text-xl tracking-wider text-text-pure uppercase font-light">
            Instituto Copleston
          </span>
        </Link>

        <nav className="hidden lg:flex items-center">
          <ul className="flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <li key={link.nombre} className="relative">
                <Link
                  href={link.href}
                  className="text-xs tracking-[0.15em] font-sans font-light text-text-muted hover:text-gold transition-colors duration-300 relative py-1 after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[1px] after:bg-gold hover:after:w-full after:transition-all after:duration-300 whitespace-nowrap uppercase"
                >
                  {link.nombre}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <div className="relative">
            <button
              ref={triggerRef}
              onClick={() => setIsCustomizerOpen(!isCustomizerOpen)}
              className={`relative text-text-muted hover:text-gold transition-colors duration-300 p-2 font-serif text-sm font-semibold tracking-wider ${
                preferences.bionic ? "after:content-['●'] after:absolute after:top-1 after:right-1 after:text-[6px] after:text-gold" : ""
              }`}
              title="Atmósfera de interfaz"
              aria-haspopup="true"
              aria-expanded={isCustomizerOpen}
              aria-controls="aa-panel"
            >
              Aa
            </button>

            {isCustomizerOpen && (
              <div
                ref={customizerRef}
                id="aa-panel"
                className="absolute top-full right-0 mt-3 w-64 bg-bg-secondary/98 backdrop-blur-xl border border-gold/25 rounded-2xl shadow-2xl shadow-black/80 p-5 z-50 text-left transition-all duration-300 scale-100 opacity-100"
                role="dialog"
                aria-label="Panel de personalización de interfaz"
              >
                <div className="absolute -top-1.5 right-4 w-3 h-3 bg-bg-secondary border-t border-l border-gold/25 rotate-45" />

                <div className="mb-4">
                  <span className="font-sans text-[9px] font-semibold tracking-widest text-gold/60 uppercase mb-3 block">
                    Atmósfera
                  </span>
                  <div className="flex gap-3 justify-between items-center">
                    <button
                      onClick={() => setTheme("sanctum")}
                      className={`w-9 h-9 rounded-full bg-[#0A0C16] border border-white/10 hover:scale-110 transition-transform ${
                        preferences.theme === "sanctum" ? "ring-2 ring-gold ring-offset-2 ring-offset-bg-secondary scale-105" : ""
                      }`}
                      title="Sanctum"
                    />
                    <button
                      onClick={() => setTheme("ethica")}
                      className={`w-9 h-9 rounded-full bg-[#F4ECD8] border border-black/10 hover:scale-110 transition-transform ${
                        preferences.theme === "ethica" ? "ring-2 ring-gold ring-offset-2 ring-offset-bg-secondary scale-105" : ""
                      }`}
                      title="Ethica"
                    />
                    <button
                      onClick={() => setTheme("immanentia")}
                      className={`w-9 h-9 rounded-full bg-[#FFFFFF] border border-black/15 hover:scale-110 transition-transform ${
                        preferences.theme === "immanentia" ? "ring-2 ring-gold ring-offset-2 ring-offset-bg-secondary scale-105" : ""
                      }`}
                      title="Luz"
                    />
                    <button
                      onClick={() => setTheme("abyssum")}
                      className={`w-9 h-9 rounded-full bg-[#000000] border border-white/20 hover:scale-110 transition-transform ${
                        preferences.theme === "abyssum" ? "ring-2 ring-gold ring-offset-2 ring-offset-bg-secondary scale-105" : ""
                      }`}
                      title="Abyssum"
                    />
                  </div>
                  <div className="flex justify-between text-[8px] text-text-muted/60 mt-1 uppercase tracking-widest font-sans font-light">
                    <span className={preferences.theme === "sanctum" ? "text-gold font-medium" : ""}>Sanctum</span>
                    <span className={preferences.theme === "ethica" ? "text-gold font-medium" : ""}>Ethica</span>
                    <span className={preferences.theme === "immanentia" ? "text-gold font-medium" : ""}>Luz</span>
                    <span className={preferences.theme === "abyssum" ? "text-gold font-medium" : ""}>Abyssum</span>
                  </div>
                </div>

                <div className="h-[1px] bg-gold/10 my-4" />

                <div className="mb-4">
                  <span className="font-sans text-[9px] font-semibold tracking-widest text-gold/60 uppercase mb-2 block">
                    Tipografía
                  </span>
                  <div className="flex bg-white/5 border border-white/10 rounded-lg overflow-hidden p-0.5">
                    <button
                      onClick={() => setFont("clasica")}
                      className={`flex-1 py-1 text-[10px] tracking-wider uppercase transition-colors rounded ${
                        preferences.font === "clasica" ? "bg-gold/20 text-gold font-medium" : "text-text-muted hover:text-text-pure"
                      }`}
                    >
                      Clásica
                    </button>
                    <button
                      onClick={() => setFont("ejecutiva")}
                      className={`flex-1 py-1 text-[10px] tracking-wider uppercase transition-colors rounded ${
                        preferences.font === "ejecutiva" ? "bg-gold/20 text-gold font-medium" : "text-text-muted hover:text-text-pure"
                      }`}
                    >
                      Ejecutiva
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <span className="font-sans text-[9px] font-semibold tracking-widest text-gold/60 uppercase mb-2 block">
                    Densidad
                  </span>
                  <div className="flex bg-white/5 border border-white/10 rounded-lg overflow-hidden p-0.5">
                    <button
                      onClick={() => setLayout("compacto")}
                      className={`flex-1 py-1 text-[10px] tracking-wider uppercase transition-colors rounded ${
                        preferences.layout === "compacto" ? "bg-gold/20 text-gold font-medium" : "text-text-muted hover:text-text-pure"
                      }`}
                    >
                      Compacto
                    </button>
                    <button
                      onClick={() => setLayout("zen")}
                      className={`flex-1 py-1 text-[10px] tracking-wider uppercase transition-colors rounded ${
                        preferences.layout === "zen" ? "bg-gold/20 text-gold font-medium" : "text-text-muted hover:text-text-pure"
                      }`}
                    >
                      Zen
                    </button>
                  </div>
                </div>

                <div className="h-[1px] bg-gold/10 my-4" />

                <div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-sans text-[10px] font-semibold text-text-pure">
                        Enfoque Biónico
                      </span>
                      <span className="font-sans text-[8px] text-text-muted/60 leading-tight">
                        Resalta letras guía para lectura acelerada
                      </span>
                    </div>
                    <button
                      onClick={() => setBionic(!preferences.bionic)}
                      className={`w-9 h-5 rounded-full relative p-0.5 transition-colors duration-300 flex-shrink-0 ${
                        preferences.bionic ? "bg-gold" : "bg-white/10 border border-white/10"
                      }`}
                    >
                      <div
                        className={`w-3.5 h-3.5 rounded-full bg-white transition-transform duration-300 ${
                          preferences.bionic ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => setIsSearchOpen(true)}
            className="text-text-muted hover:text-gold transition-colors duration-300 p-1"
            aria-label="Buscar"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>

          {siteConfig.flags.loginActivo && (
            <Link
              href="/perfil"
              onClick={handleAuthClick}
              className="inline-flex items-center justify-center bg-gold text-bg-main px-6 py-2 rounded font-sans font-semibold text-xs uppercase tracking-widest shadow-lg shadow-gold/10 hover:bg-gold-light hover:shadow-gold/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300"
            >
              {user ? getFirstName(user.displayName) : "Acceder"}
            </Link>
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden flex flex-col justify-center items-center w-8 h-8 gap-1.5 z-50 text-text-pure hover:text-gold transition-colors"
          aria-label="Abrir menú"
        >
          <span
            className={`h-[1px] w-6 bg-current transition-all duration-300 ${
              isMobileMenuOpen ? "rotate-45 translate-y-1.5" : ""
            }`}
          />
          <span
            className={`h-[1px] w-6 bg-current transition-all duration-300 ${
              isMobileMenuOpen ? "opacity-0" : ""
            }`}
          />
          <span
            className={`h-[1px] w-6 bg-current transition-all duration-300 ${
              isMobileMenuOpen ? "-rotate-45 -translate-y-2" : ""
            }`}
          />
        </button>

        <div
          className={`fixed inset-0 bg-bg-main/98 backdrop-blur-md z-40 lg:hidden flex flex-col items-center justify-center gap-8 transition-all duration-500 ${
            isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
          }`}
        >
          <ul className="flex flex-col items-center gap-6">
            {navLinks.map((link) => (
              <li key={link.nombre} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-2">
                <Link
                  href={link.href}
                  className="text-sm tracking-[0.25em] font-sans font-light text-text-muted hover:text-gold transition-colors duration-300 uppercase"
                >
                  {link.nombre}
                </Link>
              </li>
            ))}
          </ul>

          <div className="border-t border-gold/10 pt-6 mt-2 w-64 text-left">
            <span className="font-sans text-[9px] font-semibold tracking-widest text-gold/60 uppercase mb-3 block text-center">
              Atmósfera e Interfaz
            </span>
            <div className="flex gap-4 justify-center mb-4">
              <button
                onClick={() => setTheme("sanctum")}
                className={`w-8 h-8 rounded-full bg-[#0A0C16] border border-white/10 ${
                  preferences.theme === "sanctum" ? "ring-2 ring-gold ring-offset-2 ring-offset-bg-main" : ""
                }`}
                title="Sanctum"
              />
              <button
                onClick={() => setTheme("ethica")}
                className={`w-8 h-8 rounded-full bg-[#F4ECD8] border border-black/10 ${
                  preferences.theme === "ethica" ? "ring-2 ring-gold ring-offset-2 ring-offset-bg-main" : ""
                }`}
                title="Ethica"
              />
              <button
                onClick={() => setTheme("immanentia")}
                className={`w-8 h-8 rounded-full bg-[#FFFFFF] border border-black/15 ${
                  preferences.theme === "immanentia" ? "ring-2 ring-gold ring-offset-2 ring-offset-bg-main" : ""
                }`}
                title="Luz"
              />
              <button
                onClick={() => setTheme("abyssum")}
                className={`w-8 h-8 rounded-full bg-[#000000] border border-white/20 ${
                  preferences.theme === "abyssum" ? "ring-2 ring-gold ring-offset-2 ring-offset-bg-main" : ""
                }`}
                title="Abyssum"
              />
            </div>
            
            <div className="flex bg-white/5 border border-white/10 rounded-lg p-0.5 text-[10px] mb-3">
              <button
                onClick={() => setFont("clasica")}
                className={`flex-1 py-1 transition-colors rounded text-center uppercase tracking-wider ${
                  preferences.font === "clasica" ? "bg-gold/20 text-gold font-medium" : "text-text-muted hover:text-text-pure"
                }`}
              >
                Clásica
              </button>
              <button
                onClick={() => setFont("ejecutiva")}
                className={`flex-1 py-1 transition-colors rounded text-center uppercase tracking-wider ${
                  preferences.font === "ejecutiva" ? "bg-gold/20 text-gold font-medium" : "text-text-muted hover:text-text-pure"
                }`}
              >
                Ejecutiva
              </button>
            </div>

            <div className="flex items-center justify-between gap-3 bg-white/5 border border-white/10 rounded-lg p-2.5">
              <div className="flex flex-col gap-0.5">
                <span className="font-sans text-[10px] font-semibold text-text-pure leading-none">
                  Enfoque Biónico
                </span>
                <span className="font-sans text-[8px] text-text-muted/60 leading-none mt-1">
                  Resalta letras de lectura rápida
                </span>
              </div>
              <button
                onClick={() => setBionic(!preferences.bionic)}
                className={`w-8 h-4.5 rounded-full relative p-0.5 transition-colors duration-300 flex-shrink-0 ${
                  preferences.bionic ? "bg-gold" : "bg-white/10 border border-white/10"
                }`}
              >
                <div
                  className={`w-3.5 h-3.5 rounded-full bg-white transition-transform duration-300 ${
                    preferences.bionic ? "translate-x-3.5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="flex flex-col items-center gap-6 mt-4">
            <button
              className="text-text-muted hover:text-gold transition-colors duration-300 flex items-center gap-2 text-xs tracking-widest"
              onClick={() => {
                setIsMobileMenuOpen(false);
                setIsSearchOpen(true);
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              BUSCAR
            </button>
            {siteConfig.flags.loginActivo && (
              <Link
                href="/perfil"
                onClick={(e) => {
                  setIsMobileMenuOpen(false);
                  handleAuthClick(e);
                }}
                className="inline-flex items-center justify-center bg-gold text-bg-main px-10 py-3 rounded font-sans font-semibold text-xs uppercase tracking-widest shadow-lg shadow-gold/10 hover:bg-gold-light transition-all duration-300"
              >
                {user ? getFirstName(user.displayName) : "Acceder"}
              </Link>
            )}
          </div>
        </div>
      </header>

      {isAuthModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-bg-secondary/95 border border-gold/20 p-12 rounded-2xl max-w-sm w-full text-center relative shadow-2xl">
            <button
              onClick={() => setIsAuthModalOpen(false)}
              className="absolute top-4 right-5 text-white/50 hover:text-white text-2xl"
              aria-label="Cerrar"
            >
              &times;
            </button>
            <div className="auth-header text-center flex flex-col items-center">
              <Image
                src="/IMG/logo/logo-100.webp"
                alt="Copleston Shield"
                width={55}
                height={55}
                className="mb-6 filter drop-shadow-[0_0_10px_rgba(197,160,89,0.25)]"
              />
              <h2 className="font-serif text-white text-xl tracking-wider font-light mb-8">
                Acceso al Instituto Copleston
              </h2>
            </div>
            <button
              onClick={async () => {
                await loginWithGoogle();
                setIsAuthModalOpen(false);
              }}
              className="flex items-center justify-center gap-3 w-full border border-white/10 hover:border-gold/50 bg-white/5 hover:bg-white/10 text-text-pure py-3.5 px-4 rounded-lg transition-all duration-300 text-sm font-sans tracking-wide"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continuar con Google
            </button>
          </div>
        </div>
      )}

      {isSearchOpen && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[9999] flex flex-col items-center justify-start pt-24 px-6">
          <button
            onClick={() => {
              setIsSearchOpen(false);
              setSearchQuery("");
            }}
            className="absolute top-6 right-8 text-white/50 hover:text-white text-3xl"
            aria-label="Cerrar"
          >
            &times;
          </button>
          
          <div className="max-w-2xl w-full flex flex-col gap-6">
            <h2 className="font-serif text-2xl text-gold/80 tracking-widest uppercase font-light text-center">
              Buscar en Copleston
            </h2>
            <div className="relative">
              <input
                type="text"
                placeholder="Escribe para buscar artículos, libros o cursos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white/5 border border-gold/30 focus:border-gold outline-none text-text-pure px-6 py-4 rounded-xl text-lg font-sans transition-all duration-300 pr-12 shadow-lg"
                autoFocus
              />
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gold/70"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            
            {searchQuery.trim() !== "" && (
              <div className="bg-bg-secondary/40 border border-gold/10 rounded-xl p-6 min-h-[150px] flex items-center justify-center text-text-muted font-light text-sm font-sans tracking-wide">
                Buscando &quot;{searchQuery}&quot;... (Función de búsqueda completa en Artículos y Recursos)
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
